import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { identityApi, type LoginRequest, type Me } from '@/api/identity';
import { session } from '@/lib/session';
import { hasAnyPerm, hasPerm } from '@/lib/permissions';
import { qk } from '@/lib/queryKeys';

export interface AuthContextValue {
  login: (req: LoginRequest) => Promise<Me>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useSessionStatus() {
  return useSyncExternalStore(session.subscribe, session.getStatus, session.getStatus);
}

/**
 * Current user + permissions from GET /identity/me. Refetched on focus and every 5 minutes so a revoked
 * permission disappears from the UI quickly (the server re-checks every call anyway).
 */
export function useMe() {
  const status = useSessionStatus();
  return useQuery<Me>({
    queryKey: qk.me,
    queryFn: identityApi.me,
    enabled: status === 'authenticated',
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function usePermissions(): ReadonlySet<string> {
  const { data } = useMe();
  return useMemo(() => new Set(data?.permissions ?? []), [data?.permissions]);
}

/** `useCan('crm.customer.create')` or `useCan(['a', 'b'])` (any of). Supports "module.*". */
export function useCan(perm: string | readonly string[]): boolean {
  const perms = usePermissions();
  return typeof perm === 'string' ? hasPerm(perms, perm) : hasAnyPerm(perms, perm);
}
